import { FC, FocusEvent, KeyboardEvent, ReactNode, useEffect, useId, useRef, useState } from 'react';
import {
    HiOutlineMagnifyingGlass,
    HiOutlineFunnel,
    HiOutlineDocumentArrowDown,
    HiOutlinePlus,
    HiOutlineViewColumns,
    HiOutlineBookmark,
    HiXMark,
    HiArrowsUpDown,
    HiArrowUp,
    HiArrowDown,
} from 'react-icons/hi2';
import {
    DataTableColumnChooser,
    DataTableRowHeightButton,
    DataTableWrapMode,
    IDataTableChooserColumn,
} from '@srm/ui/data-table';
import ViewSwitcher, {
    IViewSwitcherOption,
    ViewIconCards,
    ViewIconCompact,
    ViewIconTable,
} from '@srm/ui/view-switcher';
import Menu from '@srm/ui/menu';
import { useDraftValue } from '@srm/ui/hooks/useDraftValue';
import { IListElement } from '@srm/ui/types/IListElement';
import { AttachView, IAttachColumn, IAttachSort } from '../../AttachGrid.types';

const VIEW_OPTIONS: IViewSwitcherOption<AttachView>[] = [
    { key: 'table', label: 'Таблица', title: 'Таблица', Icon: ViewIconTable },
    { key: 'cards', label: 'Карточки', title: 'Карточки', Icon: ViewIconCards },
    { key: 'compact', label: 'Мелкие значки', title: 'Мелкие значки', Icon: ViewIconCompact },
];

/** Пауза ввода, после которой поиск уходит в грид. */
const SEARCH_DELAY = 300;

interface AttachToolbarProps {
    /** Поиск, по которому отобраны вложения */
    search: string;
    /** Новый поиск: после паузы ввода, а при очистке и уходе из поля — сразу. Набранный текст поле держит само. */
    onSearch: (value: string) => void;
    /** Каждый введённый символ — до паузы, без отбора */
    onSearchInput?: () => void;
    sortColumns: IAttachColumn[];
    sort: IAttachSort[];
    onSortColumn: (field: string, dir: 'asc' | 'desc') => void;
    view: AttachView;
    onViewChange: (view: AttachView) => void;
    filterVisible: boolean;
    onToggleFilter: () => void;
    showFilterToggle: boolean;
    /** Есть хотя бы один заданный фильтр (для кнопки сброса в карточных видах). */
    hasActiveFilters: boolean;
    onClearFilters: () => void;
    onExport: () => void;
    /** Запуск действия «Добавить файлы» (задано → кнопка видна). */
    onAddFiles?: () => void;
    /** Колонки для «Выбора столбцов» (задано → кнопка видна). */
    chooserColumns?: IDataTableChooserColumn[];
    onColumnsApply?: (updates: Record<string, boolean>) => void;
    /** Режим переноса текста для шестерёнки настроек (задано → кнопка видна). */
    wrapMode?: DataTableWrapMode;
    onWrapModeChange?: (mode: DataTableWrapMode) => void;
    /** Открытие окна «Состояния таблицы» (StateModal — как в поисковых папках). */
    onOpenViews?: () => void;
    /** Жучок схемы. Сам компонент решает, показывать ли кнопку. */
    schemeDebug?: ReactNode;
}

const AttachToolbar: FC<AttachToolbarProps> = ({
    search,
    onSearch,
    onSearchInput,
    sortColumns,
    sort,
    onSortColumn,
    view,
    onViewChange,
    filterVisible,
    onToggleFilter,
    showFilterToggle,
    hasActiveFilters,
    onClearFilters,
    onExport,
    onAddFiles,
    chooserColumns,
    onColumnsApply,
    wrapMode,
    onWrapModeChange,
    onOpenViews,
    schemeDebug,
}) => {
    const [sortOpen, setSortOpen] = useState(false);
    const [chooserOpen, setChooserOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const {
        draft: searchText,
        change: changeSearch,
        flush: flushSearch,
        commit: commitSearch,
    } = useDraftValue(search, onSearch, SEARCH_DELAY);
    const searchExpanded = searchOpen || Boolean(searchText);
    const searchId = useId();
    const searchRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const searchButtonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (searchOpen) searchInputRef.current?.focus();
    }, [searchOpen]);

    const clearOrCloseSearch = () => {
        if (searchText) {
            setSearchOpen(true);
            commitSearch('');
            searchInputRef.current?.focus();
        } else {
            setSearchOpen(false);
            searchButtonRef.current?.focus();
        }
    };

    // фокус ушёл из поиска целиком (поле и его кнопки) — набранное отдаётся сразу; переход на очистку не отдаёт: та отдаст своё
    const onSearchBlur = (e: FocusEvent<HTMLDivElement>) => {
        if (!searchRef.current?.contains(e.relatedTarget)) flushSearch();
    };

    const onSearchKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLButtonElement>) => {
        if (e.key === 'Escape' && searchExpanded) {
            e.preventDefault();
            e.stopPropagation();
            clearOrCloseSearch();
        }
    };

    const single = sort.length === 1 ? sortColumns.find((c) => c.field === sort[0].field) : undefined;
    const sortLabel = single
        ? `Сортировка: ${single.header.toLowerCase()}`
        : sort.length > 1
          ? 'Сортировка: настроена'
          : 'Сортировка';

    // Клик по колонке: повторный клик по активной (одиночной) — переворот направления.
    const onPickSort = (field: string) => {
        const activeSingle = sort.length === 1 && sort[0].field === field;
        const col = sortColumns.find((c) => c.field === field);
        const baseDir: 'asc' | 'desc' =
            col && (col.filterKind === 'date' || col.filterKind === 'number') ? 'desc' : 'asc';
        const dir: 'asc' | 'desc' = activeSingle ? (sort[0].dir === 'asc' ? 'desc' : 'asc') : baseDir;
        onSortColumn(field, dir);
    };

    const sortItems: IListElement[] = sortColumns.map((c) => {
        const s = sort.find((x) => x.field === c.field);
        return {
            label: c.header,
            value: c.field,
            handler: () => onPickSort(c.field),
            endAdornment: s ? (
                s.dir === 'asc' ? (
                    <HiArrowUp className="atb__sort-dir" aria-hidden />
                ) : (
                    <HiArrowDown className="atb__sort-dir" aria-hidden />
                )
            ) : undefined,
        };
    });

    return (
        <div className={`atb__toolbar${searchExpanded ? ' atb__toolbar--search-open' : ''}`}>
            {onAddFiles && (
                <button type="button" className="atb__add-btn" onClick={onAddFiles}>
                    <HiOutlinePlus aria-hidden />
                    Добавить файлы
                </button>
            )}
            <div ref={searchRef} className={`atb__search${searchExpanded ? ' is-expanded' : ''}`} onBlur={onSearchBlur}>
                <button
                    ref={searchButtonRef}
                    type="button"
                    className="atb__icon-btn atb__search-toggle"
                    title="Поиск по названию файла"
                    aria-label="Поиск по названию файла"
                    aria-expanded={searchExpanded}
                    aria-controls={searchExpanded ? searchId : undefined}
                    onKeyDown={onSearchKeyDown}
                    onClick={() => {
                        if (searchExpanded && !searchText) {
                            clearOrCloseSearch();
                        } else {
                            setSearchOpen(true);
                            searchInputRef.current?.focus();
                        }
                    }}
                >
                    <HiOutlineMagnifyingGlass aria-hidden />
                </button>
                {searchExpanded && (
                    <>
                        <input
                            ref={searchInputRef}
                            id={searchId}
                            type="text"
                            aria-label="Название файла"
                            value={searchText}
                            placeholder="Название файла…"
                            onChange={(e) => {
                                onSearchInput?.();
                                changeSearch(e.target.value);
                            }}
                            onKeyDown={onSearchKeyDown}
                        />
                        <button
                            type="button"
                            className="atb__search-clear"
                            title={searchText ? 'Очистить поиск' : 'Свернуть поиск'}
                            aria-label={searchText ? 'Очистить поиск' : 'Свернуть поиск'}
                            onClick={clearOrCloseSearch}
                            onKeyDown={onSearchKeyDown}
                        >
                            <HiXMark aria-hidden />
                        </button>
                    </>
                )}
            </div>

            <div className="atb__list-actions">
                {showFilterToggle ? (
                    <button
                        type="button"
                        className={`atb__tool-btn atb__filter-btn${filterVisible ? ' is-active' : ''}`}
                        title="Фильтры"
                        aria-label="Фильтры"
                        aria-pressed={filterVisible}
                        onClick={onToggleFilter}
                    >
                        <HiOutlineFunnel aria-hidden />
                        <span>Фильтры</span>
                    </button>
                ) : (
                    // В карточных видах строки фильтров нет — даём отдельную кнопку сброса,
                    // если фильтр всё ещё задан (иначе не сбросить, не возвращаясь в таблицу).
                    hasActiveFilters && (
                        <button
                            type="button"
                            className="atb__icon-btn atb__icon-btn--clear-filter"
                            title="Сбросить фильтры"
                            aria-label="Сбросить фильтры"
                            onClick={onClearFilters}
                        >
                            <span className="atb__filterx">
                                <HiOutlineFunnel aria-hidden />
                                <HiXMark className="atb__filterx-x" aria-hidden />
                            </span>
                        </button>
                    )
                )}

                {sortColumns.length > 0 && (
                    <div className="atb__sortsel">
                        <Menu
                            list={sortItems}
                            opened={sortOpen}
                            onOpenChange={setSortOpen}
                            position="bottom-start"
                            popoverProps={{ className: 'atb__sort-menu', minWidth: 200, verticalOffset: 6 }}
                        >
                            <button
                                type="button"
                                className={`atb__tool-btn atb__sort-btn${sortOpen ? ' is-open' : ''}`}
                                title={sortLabel}
                                aria-label={sortLabel}
                            >
                                <span>{sortLabel}</span>
                                <HiArrowsUpDown className="atb__sort-chev" aria-hidden />
                            </button>
                        </Menu>
                    </div>
                )}
            </div>

            <div className="atb__display-actions">
                <ViewSwitcher value={view} options={VIEW_OPTIONS} onChange={onViewChange} ariaLabel="Вид отображения" />
                <button
                    type="button"
                    className="atb__icon-btn"
                    title="Экспорт в Excel"
                    aria-label="Экспорт в Excel"
                    onClick={onExport}
                >
                    <HiOutlineDocumentArrowDown aria-hidden />
                </button>
                {schemeDebug}
                {onOpenViews && (
                    <button
                        type="button"
                        className="atb__icon-btn"
                        title="Состояния таблицы"
                        aria-label="Состояния таблицы"
                        onClick={onOpenViews}
                    >
                        <HiOutlineBookmark aria-hidden />
                    </button>
                )}
                {wrapMode && onWrapModeChange && (
                    <DataTableRowHeightButton
                        wrapMode={wrapMode}
                        onWrapModeChange={onWrapModeChange}
                        className="atb__icon-btn"
                    />
                )}
                {chooserColumns && onColumnsApply && (
                    <DataTableColumnChooser
                        opened={chooserOpen}
                        onClose={() => setChooserOpen(false)}
                        columns={chooserColumns}
                        onApply={onColumnsApply}
                        target={
                            <button
                                type="button"
                                className={`atb__icon-btn${chooserOpen ? ' is-active' : ''}`}
                                title="Колонки"
                                aria-label="Колонки"
                                onClick={() => setChooserOpen((v) => !v)}
                            >
                                <HiOutlineViewColumns aria-hidden />
                            </button>
                        }
                    />
                )}
            </div>
        </div>
    );
};

export default AttachToolbar;
